import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, filter, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { DestinyService } from '../../../../../services';
import { CookiesService } from '../../../../../services/cookies';
import { NotificationService } from '../../../../../services/notifications/notification.service';
import { AutoCompleteItem } from '../../../../models';
import { AutocompleteDropdownComponent } from '../../../atoms';
import { InputClassComponent } from '../../input-class/input-class.component';
import { InputRangeComponent } from '../../input-range/input-range.component';
import { PopUpPasajeroComponent } from '../../pop-up-pasajero/pop-up-pasajero.component';
import { DistributionObjectA } from '../../pop-up-pasajero/pop-up-pasajero.model';
import { ParamsVueloHotel, URLVueloHotel } from '../../tabs/tabs.models';

type SearchType = 'origin' | 'destination';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_TYPE = 'FLIGHT_HOTEL';

@Component({
  selector: 'app-tab-vuelo-hotel',
  templateUrl: './tab-vuelo-hotel.component.html',
  styleUrls: ['./tab-vuelo-hotel.component.scss'],
})
export class TabVueloHotelComponent implements OnInit, OnDestroy {
  @ViewChild('originAutocomplete') originAutocomplete!: AutocompleteDropdownComponent;
  @ViewChild('destinationAutocomplete') destinationAutocomplete!: AutocompleteDropdownComponent;

  @ViewChild('popUp') popUpElement: PopUpPasajeroComponent | undefined;
  @ViewChild('childDates') childDates!: InputRangeComponent;
  @ViewChild('childClass') childClass!: InputClassComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private toDate: NgbDate | null;
  private fromDate: NgbDate | null;

  state: Record<SearchType, { list: any[]; suggestions: AutoCompleteItem[]; loading: boolean }> = {
    origin: { list: [], suggestions: [], loading: false },
    destination: { list: [], suggestions: [], loading: false },
  };

  form: FormGroup = this.fb.group({
    clase: ['economy'],
    origen: ['', [Validators.required, Validators.minLength(3)]],
    destino: ['', [Validators.required, Validators.minLength(3)]],
  });

  distribution = '';
  distributionObject: DistributionObjectA;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly destinationService: DestinyService,
    private readonly notificationService: NotificationService,
    private readonly cookiesService: CookiesService,
    private readonly fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initializeFormSubscriptions();
  }

  private initializeFormSubscriptions() {
    this.listenToInputChanges(this.origin, 'origin');
    this.listenToInputChanges(this.destination, 'destination');

    this.destinationService.vueloHotelValues$
      .pipe(
        takeUntil(this.destroy$),
        filter((data): data is { origin: string; destination: string } => !!data),
        tap((data) => {
          this.origin.setValue(data.origin, { emitEvent: false });
          this.destination.setValue(data.destination, { emitEvent: false });

          this.fetchCitySuggestions(this.extractLocation(data.origin), 'origin', false);
          this.fetchCitySuggestions(this.extractLocation(data.destination), 'destination', false);
        }),
      )
      .subscribe();
  }

  private listenToInputChanges(control: FormControl, type: SearchType) {
    control.valueChanges
      .pipe(
        map((search: string) => search?.toLowerCase().trim() ?? ''),
        tap((search) => {
          if (search.length < MIN_SEARCH_LENGTH) {
            this.state[type].list = [];
            this.state[type].suggestions = [];
          }
        }),
        filter((search) => search.length >= MIN_SEARCH_LENGTH),
        debounceTime(400),
        tap((search) => this.fetchCitySuggestions(search, type)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private fetchCitySuggestions(query: string, type: SearchType, displaySuggestions = true) {
    this.state[type].loading = displaySuggestions;

    this.destinationService
      .getDestinyPaqueteDinamico(query, SEARCH_TYPE)
      .pipe(
        take(1),
        map((data) => ({
          raw: data,
          mapped: this.mapToAutoCompleteItems(data),
        })),
        finalize(() => (this.state[type].loading = false)),
      )
      .subscribe({
        next: ({ raw, mapped }) => {
          this.state[type].list = raw;

          if (displaySuggestions) {
            this.state[type].suggestions = mapped;
            if (type === 'origin') this.originAutocomplete?.showSuggestions();
            else this.destinationAutocomplete?.showSuggestions();
          }
        },
      });
  }

  private extractLocation(location?: string) {
    return location?.split(',')[0] ?? '';
  }

  searchVueloHotel() {
    const valueClass = this.childClass.getValues();
    const newValue = valueClass.flightClass == 0 ? 'economy' : 'business';
    const valuesDateRange = this.childDates.getValuesByHotel();
    this.form.controls.clase.setValue(newValue);
    this.toDate = valuesDateRange.arrivalDate;
    this.fromDate = valuesDateRange.departureDate;

    const errosInputs = this.getErrorsForm();
    if (errosInputs.length > 0) {
      this.notificationService.showNotificacion('Error', errosInputs.join(', '), 10);
      return;
    }

    const errorHabitaciones = this.popUpElement?.isValid();
    if (!errorHabitaciones?.isValid) {
      this.notificationService.showNotificacion(
        'Error',
        errorHabitaciones?.message || 'Error en las habitaciones',
      );
      return;
    }

    const url = this.getUrlVueloHotel();
    this.navigateToResponseUrl(url);

    this.trackSearchClick();
  }

  private getErrorsForm() {
    const errors: string[] = [];

    if (this.origin.invalid) errors.push('El campo origen es obligatorio');
    if (this.destination.invalid) errors.push('El campo destino es obligatorio');

    if (!this.toDate) errors.push('La fecha final es requerido');
    if (!this.fromDate) errors.push('La fecha de inicio es requerido');

    return errors;
  }

  private getUrlVueloHotel() {
    const params = this.getParamsVueloHotel();
    return new URLVueloHotel(params, this.distribution).getUrl();
  }

  private navigateToResponseUrl(url: string) {
    if (this.isBrowser) window.open(url, '_blank');
  }

  private getParamsVueloHotel() {
    return new ParamsVueloHotel(
      this.fromDate,
      this.toDate,
      this.form,
      this.state.destination.list,
      this.state.origin.list,
    ).getParams();
  }

  private trackSearchClick() {
    const cookiesConfig = this.cookiesService.getCookiePreferences();

    if (cookiesConfig?.all || cookiesConfig?.functional) {
      let count = this.cookiesService.get<number>('countPaquetes', 0);

      count = Number.isFinite(count) ? count + 1 : 1;

      this.cookiesService.set('lastSearch', 'paquetes', 30);
      this.cookiesService.set('countPaquetes', count, 30);
    }
  }

  private mapToAutoCompleteItems(data: any[]) {
    return data.map(
      (d): AutoCompleteItem => ({
        id: d.id,
        codigo: d.value,
        country: d.country,
        title: d.label,
        children: [],
        isSelected: false,
        text: d.label,
      }),
    );
  }

  onOrigenSelect(selected: AutoCompleteItem) {
    this.origin.setValue(selected.title, { emitEvent: false });
    this.state.origin.suggestions = [];
  }

  onDestinationSelect(selected: AutoCompleteItem) {
    this.destination.setValue(selected.title, { emitEvent: false });
    this.state.destination.suggestions = [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get origin() {
    return this.form.get('origen') as FormControl<string>;
  }

  get destination() {
    return this.form.get('destino') as FormControl<string>;
  }
}
